import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Store, Sparkles, Construction, MessageCircle, Eye } from "lucide-react";
import ZeloLogo from "../components/ZeloLogo";

const WHATSAPP_NUMBER = "918758115155";

export default function EntryChoice() {
  return (
    <div className="min-h-screen bg-white relative overflow-hidden" data-testid="entry-choice-page">
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(#eee 1px, transparent 1px), linear-gradient(90deg, #eee 1px, transparent 1px)", backgroundSize: "40px 40px" }}
      />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-brand/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand/5 blur-3xl pointer-events-none" />

      <div className="relative container-zelo min-h-screen flex flex-col">
        <div className="flex items-center justify-between py-6">
          <ZeloLogo size="lg" to="/" />
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Zelo, I have a quick question.")}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-200 text-sm font-semibold text-neutral-700 hover:border-emerald-500 hover:text-emerald-600 transition"
            data-testid="entry-whatsapp-link"
          >
            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
          </a>
        </div>

        <div className="flex-1 flex flex-col justify-center py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 text-brand text-xs font-semibold tracking-wider uppercase animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              India's Next-Gen Real Estate Platform
            </div>
            <h1 className="font-heading text-5xl md:text-6xl xl:text-7xl font-medium tracking-tight leading-[1.04] mt-6 text-balance animate-fade-up" style={{ animationDelay: "60ms" }}>
              What brings you to <span className="font-russo text-brand inline-block align-baseline">Zelo</span> today?
            </h1>
            <p className="mt-6 text-lg text-neutral-500 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: "120ms" }}>
              Choose your path. Whether you're scouting retail space for your next outlet or hunting income-producing pre-leased assets — we've curated India's smartest options for both.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
            {/* RETAIL LEASING — now active/primary */}
            <Link
              to="/retail-leasing"
              data-testid="choice-retail-leasing"
              className="group relative rounded-3xl bg-brand text-white overflow-hidden p-8 md:p-10 hover:-translate-y-1 transition-all duration-500 shadow-brand"
            >
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase opacity-90">
                    <Store className="w-4 h-4" /> Live · 1,070+ Spaces
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white/15 group-hover:bg-white/25 transition flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl xl:text-5xl font-medium leading-tight mt-10 text-balance">
                  Retail Leasing
                </h2>
                <p className="mt-4 text-white/85 text-base leading-relaxed max-w-md">
                  Premium high-street and ground-floor showroom spaces for India's most demanding retail brands. Pin-drop ready.
                </p>
                <div className="mt-10 grid grid-cols-3 gap-4 text-sm">
                  <Stat n="1,070+" l="Spaces" />
                  <Stat n="15+" l="States" />
                  <Stat n="Tier 1–3" l="Cities" />
                </div>
              </div>
            </Link>

            {/* PRE-LEASED — Under Development - Take a Preview */}
            <Link
              to="/pre-leased"
              data-testid="choice-pre-leased"
              className="group relative rounded-3xl bg-white border border-neutral-200 p-8 md:p-10 hover:-translate-y-1 transition-all duration-500 hover:border-brand/30 hover:shadow-soft"
            >
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-neutral-500">
                  <Construction className="w-4 h-4" /> Under Development
                </div>
                <div className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">Beta</div>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl xl:text-5xl font-medium leading-tight mt-10 text-neutral-900 text-balance">
                Pre-Leased Properties
              </h2>
              <p className="mt-4 text-neutral-500 text-base leading-relaxed max-w-md">
                Rent-yielding, brand-tenanted commercial assets — already leased, already paying. We're polishing the experience. Take a sneak peek.
              </p>
              <div className="mt-10 grid grid-cols-3 gap-4 text-sm">
                <PreviewStat n="8.3%" l="Avg ROI" />
                <PreviewStat n="9 yr" l="Lock-in" />
                <PreviewStat n="₹1.4L+" l="Monthly" />
              </div>
              <div className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-50 text-brand text-sm font-semibold group-hover:bg-brand group-hover:text-white transition" data-testid="pre-leased-preview-cta">
                <Eye className="w-4 h-4" /> Take a Preview <ArrowUpRight className="w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>

        <div className="py-6 text-center text-xs text-neutral-400">
          © {new Date().getFullYear()} Zelo Leaseworks LLP · Brand-tenanted, income-producing real estate.
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-heading text-2xl font-medium">{n}</div>
      <div className="text-xs uppercase tracking-wider opacity-70 mt-0.5">{l}</div>
    </div>
  );
}

function PreviewStat({ n, l }) {
  return (
    <div>
      <div className="font-heading text-xl font-medium text-neutral-900">{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-0.5">{l}</div>
    </div>
  );
}
